import React, { useState, useEffect } from 'react';
import { Trash2, Plus, Edit2, Check, X } from 'lucide-react';
import Modal from '../../components/Modal';
import { apiClient } from '../../config/api';

const AdminBrandsPage = () => {
    const [brands, setBrands] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newBrand, setNewBrand] = useState({ name: '', oem_id: '' });
    const [editingBrand, setEditingBrand] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [duplicateWarning, setDuplicateWarning] = useState('');
    const [availableOEMs, setAvailableOEMs] = useState([]);

    useEffect(() => {
        fetchBrands();
        fetchOEMs();
    }, []);

    const fetchOEMs = async () => {
        try {
            const data = await apiClient.get('/oems');
            setAvailableOEMs((data || []).filter(o => o.is_active !== false));
        } catch (error) {
            console.error('Error fetching OEMs:', error);
        }
    };

    const fetchBrands = async () => {
        try {
            setLoading(true);
            const data = await apiClient.get('/brands');
            setBrands(data || []);
        } catch (error) {
            console.error('Error fetching brands:', error);
            setError('Failed to fetch brands');
        } finally {
            setLoading(false);
        }
    };

    // Function to normalize strings for comparison
    const normalizeString = (str) => {
        return str.toLowerCase().trim().replace(/\s+/g, ' ');
    };

    // Function to calculate similarity between two strings
    const calculateSimilarity = (str1, str2) => {
        const s1 = normalizeString(str1);
        const s2 = normalizeString(str2);
        
        // Exact match
        if (s1 === s2) return 1.0;
        
        // Check if one contains the other
        if (s1.includes(s2) || s2.includes(s1)) {
            const shorter = Math.min(s1.length, s2.length);
            const longer = Math.max(s1.length, s2.length);
            return shorter / longer;
        }
        
        // Calculate Levenshtein distance similarity
        const maxLen = Math.max(s1.length, s2.length);
        if (maxLen === 0) return 1.0;
        
        const distance = levenshteinDistance(s1, s2);
        return 1 - (distance / maxLen);
    };

    // Levenshtein distance calculation
    const levenshteinDistance = (str1, str2) => {
        const matrix = [];
        for (let i = 0; i <= str2.length; i++) {
            matrix[i] = [i];
        }
        for (let j = 0; j <= str1.length; j++) {
            matrix[0][j] = j;
        }
        for (let i = 1; i <= str2.length; i++) {
            for (let j = 1; j <= str1.length; j++) {
                if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j - 1] + 1,
                        matrix[i][j - 1] + 1,
                        matrix[i - 1][j] + 1
                    );
                }
            }
        }
        return matrix[str2.length][str1.length];
    };

    // Check for duplicate or similar brands
    const checkForDuplicates = (brandName, excludeId = null) => {
        if (!brandName || !brandName.trim()) {
            setDuplicateWarning('');
            return { isDuplicate: false, isSimilar: false, similarBrands: [] };
        }

        const normalizedInput = normalizeString(brandName);
        const similarBrands = [];
        let isDuplicate = false;
        let isSimilar = false;

        brands.forEach(brand => {
            // Skip the brand being edited
            if (excludeId && brand.id === excludeId) return;

            const normalizedBrand = normalizeString(brand.name);
            const similarity = calculateSimilarity(brandName, brand.name);

            // Exact duplicate (case-insensitive)
            if (normalizedInput === normalizedBrand) {
                isDuplicate = true;
                similarBrands.push({ name: brand.name, similarity: 1.0 });
            }
            // Similar name (similarity >= 0.7 or contains/contained)
            else if (similarity >= 0.7 || normalizedInput.includes(normalizedBrand) || normalizedBrand.includes(normalizedInput)) {
                isSimilar = true;
                similarBrands.push({ name: brand.name, similarity });
            }
        });

        return { isDuplicate, isSimilar, similarBrands };
    };

    const handleAdd = async () => {
        if (!newBrand.name || !newBrand.name.trim()) {
            setError('Brand name is required');
            return;
        }

        // Check for duplicates before submitting
        const { isDuplicate, isSimilar, similarBrands } = checkForDuplicates(newBrand.name);
        
        if (isDuplicate) {
            setError(`A brand with the name "${similarBrands[0].name}" already exists. Please choose a different name.`);
            return;
        }

        if (isSimilar) {
            const similarNames = similarBrands.map(b => b.name).join(', ');
            const confirmed = window.confirm(
                `Warning: Similar brand names already exist: ${similarNames}\n\nAre you sure you want to create this brand?`
            );
            if (!confirmed) {
                return;
            }
        }

        try {
            setError('');
            setDuplicateWarning('');
            const createdBrand = await apiClient.post('/brands', {
                name: newBrand.name.trim(),
                oem_id: newBrand.oem_id && newBrand.oem_id !== '' ? newBrand.oem_id : null
            });
            setBrands([...brands, createdBrand]);
            setIsModalOpen(false);
            setNewBrand({ name: '' });
            setDuplicateWarning('');
        } catch (error) {
            console.error('Error adding brand:', error);
            setError('Failed to create brand');
        }
    };

    const handleUpdate = async () => {
        if (!editingBrand || !editingBrand.name || !editingBrand.name.trim()) {
            setError('Brand name is required');
            return;
        }

        // Check for duplicates before submitting (excluding current brand)
        const { isDuplicate, isSimilar, similarBrands } = checkForDuplicates(editingBrand.name, editingBrand.id);
        
        if (isDuplicate) {
            setError(`A brand with the name "${similarBrands[0].name}" already exists. Please choose a different name.`);
            return;
        }

        if (isSimilar) {
            const similarNames = similarBrands.map(b => b.name).join(', ');
            const confirmed = window.confirm(
                `Warning: Similar brand names already exist: ${similarNames}\n\nAre you sure you want to update this brand?`
            );
            if (!confirmed) {
                return;
            }
        }

        try {
            setError('');
            setDuplicateWarning('');
            const updatedBrand = await apiClient.put(`/brands/${editingBrand.id}`, {
                name: editingBrand.name.trim(),
                oem_id: editingBrand.oem_id && editingBrand.oem_id !== '' ? editingBrand.oem_id : null
            });
            setBrands(brands.map(b => b.id === updatedBrand.id ? updatedBrand : b));
            setEditingBrand(null);
            setDuplicateWarning('');
        } catch (error) {
            console.error('Error updating brand:', error);
            setError('Failed to update brand');
        }
    };

    const handleDelete = async (brandId) => {
        if (!window.confirm('Are you sure you want to delete this brand? This will remove all associations with dealer locations.')) return;

        try {
            await apiClient.delete(`/brands/${brandId}`);
            setBrands(brands.filter(b => b.id !== brandId));
        } catch (error) {
            console.error('Error deleting brand:', error);
            alert(error.message || 'Failed to delete brand');
        }
    };

    const openAddModal = () => {
        setNewBrand({ name: '', oem_id: '' });
        setError('');
        setDuplicateWarning('');
        setIsModalOpen(true);
    };

    const openEditModal = (brand) => {
        setEditingBrand({ ...brand });
        setError('');
        setDuplicateWarning('');
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setNewBrand({ name: '', oem_id: '' });
        setEditingBrand(null);
        setError('');
        setDuplicateWarning('');
    };

    // Handle input change with duplicate checking
    const handleNameChange = (value) => {
        if (editingBrand) {
            setEditingBrand({ ...editingBrand, name: value });
        } else {
            setNewBrand({ ...newBrand, name: value });
        }
        setError('');

        // Check for duplicates as user types
        if (value && value.trim()) {
            const { isDuplicate, isSimilar, similarBrands } = checkForDuplicates(value, editingBrand?.id);
            if (isDuplicate) {
                setDuplicateWarning(`⚠️ A brand with this exact name already exists: "${similarBrands[0].name}"`);
            } else if (isSimilar) {
                const similarNames = similarBrands.map(b => b.name).join(', ');
                setDuplicateWarning(`⚠️ Warning: Similar brand names found: ${similarNames}`);
            } else {
                setDuplicateWarning('');
            }
        } else {
            setDuplicateWarning('');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-500">Loading brands...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-xl font-bold">Brands Management</h3>
                    <p className="text-gray-600 mt-1">Manage brands/franchises that can be associated with dealer locations</p>
                </div>
                <button
                    onClick={openAddModal}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                    <Plus size={16} />
                    Add Brand
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                </div>
            )}

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Brand Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                OEM
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {brands.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                                    No brands found. Create your first brand to get started.
                                </td>
                            </tr>
                        ) : (
                            brands.map((brand) => (
                                <tr key={brand.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {brand.id}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{brand.name}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-600">
                                            {brand.oem_name || <span className="text-gray-400 italic">Independent</span>}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            onClick={() => openEditModal(brand)}
                                            className="text-blue-600 hover:text-blue-900 mr-4"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(brand.id)}
                                            className="text-red-600 hover:text-red-900"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <Modal 
                isOpen={isModalOpen} 
                onClose={closeModal} 
                title={editingBrand ? 'Edit Brand' : 'Add New Brand'}
            >
                <div className="space-y-4">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                            {error}
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium mb-1">Brand Name *</label>
                        <input
                            type="text"
                            value={editingBrand ? editingBrand.name : newBrand.name}
                            onChange={(e) => handleNameChange(e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                duplicateWarning ? 'border-yellow-400' : ''
                            }`}
                            placeholder="Enter brand name"
                            autoFocus
                        />
                        {duplicateWarning && (
                            <div className="mt-2 bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded-lg text-sm">
                                {duplicateWarning}
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">OEM (Optional)</label>
                        <select
                            value={editingBrand ? (editingBrand.oem_id || '') : (newBrand.oem_id || '')}
                            onChange={(e) => {
                                if (editingBrand) {
                                    setEditingBrand({ ...editingBrand, oem_id: e.target.value });
                                } else {
                                    setNewBrand({ ...newBrand, oem_id: e.target.value });
                                }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">No OEM (Independent)</option>
                            {availableOEMs.map(oem => (
                                <option key={oem.id} value={oem.id}>{oem.name}</option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Select the OEM that owns this brand</p>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                        <button
                            onClick={closeModal}
                            className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={editingBrand ? handleUpdate : handleAdd}
                            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                        >
                            {editingBrand ? 'Update' : 'Add'} Brand
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AdminBrandsPage;

